import React, { useState } from 'react';

// Sample area guide data store per township
const AREA_GUIDE_DATA = {
  "horizon-hills": {
    developer: "Gamuda Land & UEM Sunrise",
    tenure: "Freehold",
    density: "Low Density Gated Township",
    landArea: "1,200 Acres",
    security: "24/7 Multi-Tiered Patrol & CCTV",
    description: "Horizon Hills is an exclusive, award-winning gated residential township built around an 18-hole international signature golf course. It offers peaceful luxury living with rolling hills, lush parks, and top-tier clubhouse facilities.",
    travelTimes: [
      { icon: "bi-car-front-fill", label: "Tuas Second Link Checkpoint", time: "8 - 10 Mins" },
      { icon: "bi-building-check", label: "EduCity & International Schools", time: "5 Mins" },
      { icon: "bi-bag-check-fill", label: "AEON & Lotus's Bukit Indah", time: "5 Mins" },
      { icon: "bi-hospital-fill", label: "Columbia Asia / Gleneagles Hospital", time: "7 Mins" },
      { icon: "bi-airplane-fill", label: "Senai International Airport", time: "25 Mins" },
    ],
    amenities: {
      education: ["Marlborough College Malaysia", "Raffles American School", "EduCity Hub"],
      shopping: ["Horizon Avenue Commercial", "AEON Mall Bukit Indah", "Eco Galleria"],
      healthcare: ["Columbia Asia Hospital", "Gleneagles Hospital Medini"],
      leisure: ["18-Hole Signature Golf Course", "Horizon Hills Clubhouse", "Sireh Park"]
    },
    marketStats: {
      avgPriceLanded: "RM 950,000 - RM 2.8 Million",
      avgPsf: "RM 550 - RM 850 / sqft",
      rentalYield: "4.2% - 5.5% p.a.",
      subPrecincts: ["Golf East", "Canal Garden", "The Cove", "D'Claymore", "Valley West", "The Hills"]
    }
  },
  "bukit-indah": {
    developer: "SP Setia",
    tenure: "Freehold",
    density: "Established Commercial & Residential Hub",
    landArea: "1,500 Acres",
    security: "Gated & Guarded Precincts",
    description: "Bukit Indah is the central heartbeat of Iskandar Puteri. Famed for its award-winning 20-acre town park, AEON Mall, and unmatched commercial convenience, it is a top choice for families and Singapore commuters.",
    travelTimes: [
      { icon: "bi-car-front-fill", label: "Tuas Second Link Checkpoint", time: "10 Mins" },
      { icon: "bi-shield-check", label: "Woodlands CIQ Checkpoint", time: "15 Mins" },
      { icon: "bi-bag-check-fill", label: "AEON Mall & Lotus's Bukit Indah", time: "2 Mins" },
      { icon: "bi-hospital-fill", label: "Columbia Asia Hospital", time: "5 Mins" },
      { icon: "bi-airplane-fill", label: "Senai International Airport", time: "22 Mins" },
    ],
    amenities: {
      education: ["SJK(C) Ming Chih", "SJK(C) Pei Hwa", "Bukit Indah National Schools"],
      shopping: ["AEON Mall Bukit Indah", "Lotus's Hypermarket", "TF Value-Mart"],
      healthcare: ["Columbia Asia Hospital", "Kensington Green Specialist"],
      leisure: ["20-Acre Bukit Indah Town Park", "Bukit Indah Recreation Center"]
    },
    marketStats: {
      avgPriceLanded: "RM 680,000 - RM 1.5 Million",
      avgPsf: "RM 450 - RM 650 / sqft",
      rentalYield: "4.8% - 6.0% p.a.",
      subPrecincts: ["Indah 1 - Indah 15", "Sky Executive Suites", "Sky Loft", "Indah Walk"]
    }
  }
};

// Default fallback guide data generator
const getDefaultGuide = (title) => ({
  developer: "Premier Developer Consortium",
  tenure: "Freehold",
  density: "Low-to-Medium Density Precinct",
  landArea: "Established Township",
  security: "24/7 Security Patrol & Guarded Entry",
  description: `${title} is one of Iskandar Puteri's prime residential communities, offering modern architecture, excellent highway connectivity, and close proximity to top amenities.`,
  travelTimes: [
    { icon: "bi-car-front-fill", label: "Tuas Second Link Checkpoint", time: "10 Mins" },
    { icon: "bi-building-check", label: "EduCity & Schools", time: "8 Mins" },
    { icon: "bi-bag-check-fill", label: "Shopping Malls & Hypermarkets", time: "5 Mins" },
    { icon: "bi-hospital-fill", label: "Gleneagles / Columbia Asia Hospital", time: "8 Mins" },
  ],
  amenities: {
    education: ["International Schools", "National Primary & Secondary Schools"],
    shopping: ["Township Commercial Square", "Nearby Hypermarkets"],
    healthcare: ["Regional Specialist Hospitals", "Local Medical Clinics"],
    leisure: ["Community Green Parks", "Sports & Fitness Clubs"]
  },
  marketStats: {
    avgPriceLanded: "RM 650,000 - RM 1.8 Million",
    avgPsf: "RM 480 - RM 720 / sqft",
    rentalYield: "4.5% - 5.8% p.a.",
    subPrecincts: ["Phase 1", "Phase 2", "Gated Villas", "Town Center Suites"]
  }
});

const AreaGuideModal = ({ area, agent, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!area) return null;

  const slug = (area.title || '').toLowerCase().replace(/\s+/g, '-');
  const guide = AREA_GUIDE_DATA[slug] || getDefaultGuide(area.title);

  const whatsappUrl = `https://wa.me/${agent?.whatsappNumber || '60163288120'}?text=Hi%20Nicole,%20I'd%20like%20to%20learn%20more%20and%20book%20an%20Area%20Tour%20for%20${encodeURIComponent(area.title)}`;

  return (
    <div className="area-guide-modal-overlay" onClick={onClose}>
      <div className="area-guide-modal-container" onClick={(e) => e.stopPropagation()}>
        
        {/* Close Button */}
        <button className="area-guide-close-btn" onClick={onClose} aria-label="Close modal">
          <i className="bi bi-x-lg"></i>
        </button>

        {/* Modal Hero Banner */}
        <div 
          className="area-guide-hero"
          style={{ 
            backgroundImage: `linear-gradient(rgba(9, 13, 22, 0.82), rgba(16, 24, 38, 0.95)), url(${area.bannerUrl || area.imageUrl || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80'})` 
          }}
        >
          <div className="guide-hero-badge">TOWNSHIP & AREA GUIDE</div>
          <h2 className="guide-hero-title">{area.title}</h2>
          <div className="guide-hero-chips">
            <span><i className="bi bi-shield-lock-fill"></i> {guide.security}</span>
            <span><i className="bi bi-key-fill"></i> {guide.tenure}</span>
            <span><i className="bi bi-geo-alt-fill"></i> Iskandar Puteri</span>
          </div>
        </div>

        {/* Modal Navigation Tabs */}
        <div className="area-guide-tabs">
          <button 
            className={`guide-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="bi bi-info-circle-fill"></i> Overview
          </button>
          <button 
            className={`guide-tab-btn ${activeTab === 'connectivity' ? 'active' : ''}`}
            onClick={() => setActiveTab('connectivity')}
          >
            <i className="bi bi-car-front-fill"></i> Distance & Travel
          </button>
          <button 
            className={`guide-tab-btn ${activeTab === 'amenities' ? 'active' : ''}`}
            onClick={() => setActiveTab('amenities')}
          >
            <i className="bi bi-building-fill"></i> Amenities
          </button>
          <button 
            className={`guide-tab-btn ${activeTab === 'market' ? 'active' : ''}`}
            onClick={() => setActiveTab('market')}
          >
            <i className="bi bi-graph-up-arrow"></i> Price & Precincts
          </button>
        </div>

        {/* Modal Tab Contents */}
        <div className="area-guide-content">
          
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="guide-tab-pane fade-in">
              <p className="guide-description-text">{guide.description}</p>

              <div className="guide-grid-2">
                <div className="guide-info-card">
                  <div className="info-icon"><i className="bi bi-building"></i></div>
                  <div className="info-text">
                    <span className="info-label">Master Developer</span>
                    <span className="info-val">{guide.developer}</span>
                  </div>
                </div>

                <div className="guide-info-card">
                  <div className="info-icon"><i className="bi bi-bounding-box-circles"></i></div>
                  <div className="info-text">
                    <span className="info-label">Total Township Area</span>
                    <span className="info-val">{guide.landArea}</span>
                  </div>
                </div>

                <div className="guide-info-card">
                  <div className="info-icon"><i className="bi bi-shield-check"></i></div>
                  <div className="info-text">
                    <span className="info-label">Security & Density</span>
                    <span className="info-val">{guide.density}</span>
                  </div>
                </div>

                <div className="guide-info-card">
                  <div className="info-icon"><i className="bi bi-file-earmark-check"></i></div>
                  <div className="info-text">
                    <span className="info-label">Land Tenure</span>
                    <span className="info-val">{guide.tenure}</span>
                  </div>
                </div>
              </div>

              {area.highlights && area.highlights.length > 0 && (
                <div className="guide-highlights-box">
                  <h4 className="box-title"><i className="bi bi-star-fill"></i> Key Township Highlights</h4>
                  <ul className="guide-highlights-list">
                    {area.highlights.map((hl, idx) => (
                      <li key={idx}>
                        <i className="bi bi-check-circle-fill"></i> {hl}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: CONNECTIVITY & DISTANCE */}
          {activeTab === 'connectivity' && (
            <div className="guide-tab-pane fade-in">
              <h4 className="guide-pane-title">Estimated Travel Times from {area.title}</h4>
              <div className="travel-times-list">
                {guide.travelTimes.map((item, idx) => (
                  <div className="travel-item" key={idx}>
                    <div className="travel-left">
                      <div className="travel-icon"><i className={`bi ${item.icon}`}></i></div>
                      <span className="travel-name">{item.label}</span>
                    </div>
                    <span className="travel-time-badge">{item.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 3: AMENITIES */}
          {activeTab === 'amenities' && (
            <div className="guide-tab-pane fade-in">
              <h4 className="guide-pane-title">Nearby Essential Services & Lifestyle Hubs</h4>
              <div className="amenities-category-grid">
                
                <div className="amenity-cat-card">
                  <h5 className="cat-title"><i className="bi bi-mortarboard-fill"></i> Schools & Universities</h5>
                  <ul>
                    {guide.amenities.education.map((item, idx) => (
                      <li key={idx}><i className="bi bi-dot"></i> {item}</li>
                    ))}
                  </ul>
                </div>

                <div className="amenity-cat-card">
                  <h5 className="cat-title"><i className="bi bi-bag-fill"></i> Shopping & Dining</h5>
                  <ul>
                    {guide.amenities.shopping.map((item, idx) => (
                      <li key={idx}><i className="bi bi-dot"></i> {item}</li>
                    ))}
                  </ul>
                </div>

                <div className="amenity-cat-card">
                  <h5 className="cat-title"><i className="bi bi-hospital-fill"></i> Medical Centers</h5>
                  <ul>
                    {guide.amenities.healthcare.map((item, idx) => (
                      <li key={idx}><i className="bi bi-dot"></i> {item}</li>
                    ))}
                  </ul>
                </div>

                <div className="amenity-cat-card">
                  <h5 className="cat-title"><i className="bi bi-tree-fill"></i> Parks & Recreation</h5>
                  <ul>
                    {guide.amenities.leisure.map((item, idx) => (
                      <li key={idx}><i className="bi bi-dot"></i> {item}</li>
                    ))}
                  </ul>
                </div>

              </div>
            </div>
          )}

          {/* TAB 4: MARKET STATS & PRECINCTS */}
          {activeTab === 'market' && (
            <div className="guide-tab-pane fade-in">
              <h4 className="guide-pane-title">Market Benchmark & Sub-Precinct Breakdown</h4>

              <div className="stats-cards-row">
                <div className="stat-card">
                  <span className="stat-label">Landed Price Range</span>
                  <span className="stat-value">{guide.marketStats.avgPriceLanded}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Average PSF</span>
                  <span className="stat-value">{guide.marketStats.avgPsf}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Estimated Rental Yield</span>
                  <span className="stat-value">{guide.marketStats.rentalYield}</span>
                </div>
              </div>

              <div className="precincts-section">
                <h5 className="precincts-title"><i className="bi bi-houses-fill"></i> Featured Sub-Precincts & Clusters</h5>
                <div className="precincts-tags">
                  {guide.marketStats.subPrecincts.map((precinct, idx) => (
                    <span className="precinct-tag" key={idx}>
                      <i className="bi bi-geo"></i> {precinct}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
};

export default AreaGuideModal;
